/** @format */
import { ReactNode } from 'react';

export function* generateInsertIndexes(): Generator<number> {
    let i = 4; // we want to insert the card in the 5th position, but arrays are 0-indexed.
    while (true) {
        yield i;
        i += 16;
    }
}

/**
 * Generates the insert positions for the contentful cards.
 *
 * The insert positions are calculated as array indexes, so the first insert position is 4, which will insert the card
 * in the 5th position etc.
 *
 * The complexity in this function is that not only do we need to account for the offset, we need to account for the
 * previously inserted cards. However, this isn't as simple as seeing if the insert position is greater than the offset,
 * because the offset does not include the previously inserted cards.
 *
 * @param resultsCount The number of results on the page.
 * @param offset The item offset (i.e. total results on previous pages)
 */
export function* generatePageInsertIndexes(
    resultsCount: number,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
    offset: number = 0
): Generator<{ insertIndex: number; itemIndex: number }> {
    const getInsertIndex = generateInsertIndexes();
    let offsetItemCount = 0;
    let itemIndex = 0;

    while (true) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
        const nextInsertIndex = getInsertIndex.next().value;

        // Simulate the real offset (including inserted cards, by incrementing the offsetItemCount)
        if (nextInsertIndex < offset + offsetItemCount) {
            offsetItemCount += 1;
            itemIndex += 1;
            continue;
        }

        // Calculate the _relative_ insert index (i.e. the insert index relative to the results on the page)
        const insertIndex = nextInsertIndex - offsetItemCount - offset;

        // If the insert index is greater than the number of results on the page, we're done.
        if (insertIndex > resultsCount + itemIndex - 1) {
            break;
        }

        yield {
            insertIndex,
            itemIndex,
        };

        itemIndex += 1;
    }
}

export const insertContentfulTiles = (
    results: ReactNode[],
    contentfulTiles: ReactNode[],
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
    offset: number = 0
) => {
    const newCards = [...results];
    const insertPositions = Array.from(
        generatePageInsertIndexes(results.length, offset)
    );

    insertPositions.forEach(({ insertIndex, itemIndex }) => {
        if (!contentfulTiles?.[itemIndex]) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Legacy code: do not use as an example.
        newCards.splice(insertIndex, 0, contentfulTiles[itemIndex]);
    });

    return newCards;
};
